<template>
  <div class="border-b border-zyxel-gray-500">
    <div class="flex justify-between items-start">
      <h3 class="ml-3 sm:ml-6 mt-6 mb-6 text-zyxel-gray-900 font-bold">
        {{ $t('messages.account_info.profile') }}
      </h3>
      <div class="hidden sm:block mr-6 mt-6 mb-6 md:block">
        <div v-if="!isEditing">
          <button
            class="text-zyxel-green-700 font-bold cursor-pointer"
            @click="toggleEdit"
          >
            {{ $t('messages.common.link_edit_profile') }}
          </button>
        </div>
        <div
          v-else
          class="flex-row block"
        >
          <button
            class="text-zyxel-green-700 font-bold cursor-pointer"
            @click="cancelEdit"
          >
            {{ $t('messages.common.link_cancel') }}
          </button>
          <button
            class="cursor-not-allowed text-zyxel-gray-800 font-bold ml-2"
            :class="{ 'cursor-pointer text-zyxel-green-700': !isDemoAccount }"
            @click="saveEdit"
          >
            {{ $t('messages.common.link_save') }}
          </button>
        </div>
      </div>
    </div>
    <div class="caption">
      <div v-if="isEditing">
        <dl class="grid sm:grid-cols-[auto_1fr] gap-x-4 gap-y-1 sm:gap-y-2">
          <dt class="flex items-center text-zyxel-gray-900 max-w-[240px] ml-3 sm:ml-6 sm:mb-5 h-8 sm:h-12">
            {{
              $t('messages.text_input.first_name') }}
            <span class="ml-2 text-zyxel-red-700">*</span>
          </dt>
          <dd class="px-3 sm:px-0 sm:mr-6">
            <label
              for="first-name-input"
              class="sr-only"
            >{{ $t('messages.text_input.first_name') }}</label>
            <input
              id="first-name-input"
              v-model="editedUser.first_name"
              :class="['text-input p-4 text-left h-12 w-full', { 'error-input': errors.first_name }]"
              type="text"
            >
            <span
              v-if="errors.first_name"
              class="error-message mt-1 b2 block text-left w-full"
            >{{
              $t(errors_msg.first_name) }}</span>
          </dd>
          <dt class="flex items-center text-zyxel-gray-900 max-w-[240px] ml-3 sm:ml-6 sm:mb-5 h-8 sm:h-12">
            {{
              $t('messages.text_input.last_name') }}
            <span class="ml-2 text-zyxel-red-700">*</span>
          </dt>
          <dd class="px-3 sm:px-0 sm:mr-6">
            <label
              for="last-name-input"
              class="sr-only"
            >{{ $t('messages.text_input.last_name') }}</label>
            <input
              id="last-name-input"
              v-model="editedUser.last_name"
              :class="['text-input p-4 text-left h-12 w-full', { 'error-input': errors.last_name }]"
              type="text"
            >
            <span
              v-if="errors.last_name"
              class="error-message mt-1 b2 block text-left w-full"
            >{{
              $t(errors_msg.last_name) }}</span>
          </dd>
          <dt class="flex items-center text-zyxel-gray-900 max-w-[240px] ml-3 sm:ml-6 sm:mb-5 h-8 sm:h-12">
            {{
              $t('messages.text_input.email') }}
            <span
              v-if="user.login_method === 'password'"
              class="ml-2 text-zyxel-red-700"
            >*</span>
          </dt>
          <dd class="px-3 sm:px-0 sm:mr-6">
            <label
              for="email-input"
              class="sr-only"
            >{{ $t('messages.text_input.email') }}</label>
            <input
              id="email-input"
              v-model="editedUser.email"
              :class="['text-input p-4 text-left h-12 w-full', { 'error-input': errors.email, 'bg-zyxel-gray-300': user.login_method !== 'password' }]"
              type="text"
              inputmode="email"
              :disabled="user.login_method !== 'password'"
            >
            <span
              v-if="errors.email"
              class="error-message mt-1 b2 block text-left w-full"
            >{{
              $t(errors_msg.email) }}</span>
          </dd>
          <dt class="flex items-center text-zyxel-gray-900 max-w-[240px] ml-3 sm:ml-6 sm:mb-5 h-8 sm:h-12">
            {{
              $t('messages.text_input.country_region') }}
            <span class="ml-2 text-zyxel-red-700">*</span>
          </dt>
          <dd class="px-3 sm:px-0 sm:mr-6">
            <CountrySelect v-model="editedUser.country" />
          </dd>
          <dt class="flex items-center text-zyxel-gray-900 max-w-[240px] ml-3 sm:ml-6 sm:mb-5 h-8 sm:h-12">
            {{
              $t('messages.text_input.contact') }}
          </dt>
          <dd class="px-3 sm:px-0 sm:mr-6">
            <label
              for="contact-input"
              class="sr-only"
            >{{ $t('messages.text_input.contact') }}</label>
            <input
              id="contact-input"
              v-model="editedUser.contact"
              :class="['text-input p-4 text-left h-12 w-full', { 'error-input': errors.contact }]"
              type="text"
              inputmode="email"
              placeholder="name@example.com"
            >
            <span
              v-if="errors.contact"
              class="error-message mt-1 b2 block text-left w-full"
            >{{
              $t(errors_msg.contact) }}</span>
            <span class="b3 block text-left flex items-start mt-1 mb-4"><img
              class="h-3 mt-1 mr-1"
              src="@/assets/svg/icon_24px_info.svg"
              alt=""
            >{{ $t('messages.text_input.contact_info') }}</span>
            <div
              v-if="!isDemoAccount"
              class="flex items-center gap-2 text-zyxel-gray-800 mb-4"
            >
              <span>{{ $t('messages.delete_account.confirm_description') }}</span>
              <button
                class="text-zyxel-green-700 font-bold cursor-pointer"
                @click="toggleDelete"
              >
                {{ $t('messages.delete_account.label') }}
              </button>
            </div>
          </dd>
        </dl>
      </div>
      <div v-else>
        <dl class="grid sm:grid-cols-[auto_1fr] gap-x-4 gap-y-1 sm:gap-y-5 px-4 pb-4 flex items-center">
          <dt class="text-zyxel-gary-900 max-w-[240px] sm:ml-6">
            {{ $t('messages.text_input.first_name') }}
          </dt>
          <dd :class="{'sm:px-2': true, 'px-0': true, 'text-ellipsis': true}">
            {{ user.first_name }}
          </dd>
          <dt class="text-zyxel-gray-900 max-w-[240px] mt-3 sm:mt-0 sm:ml-6">
            {{ $t('messages.text_input.last_name') }}
          </dt>
          <dd :class="{'sm:px-2': true, 'px-0': true, 'text-ellipsis': true}">
            {{ user.last_name }}
          </dd>
          <dt class="text-zyxel-gray-900 max-w-[240px] mt-3 sm:mt-0 sm:ml-6">
            {{ $t('messages.text_input.email') }}
          </dt>
          <dd :class="{'sm:px-2': true, 'px-0': true, 'text-ellipsis': true}">
            {{ user.email }}
          </dd>
          <dt class="text-zyxel-gray-900 max-w-[240px] mt-3 sm:mt-0 sm:ml-6">
            {{ $t('messages.text_input.country_region') }}
          </dt>
          <dd>
            <div v-if="userCountryLower">
              <CountrySelect
                v-model="userCountryLower"
                :disabled="true"
              />
            </div>
          </dd>
          <dt class="text-zyxel-gray-900 max-w-[240px] mt-3 sm:mt-0 sm:ml-6">
            {{ $t('messages.text_input.contact') }}
          </dt>
          <dd :class="{'sm:px-2': true, 'px-0': true}">
            {{ user.contact }}
          </dd>
        </dl>
      </div>
      <div class="sm:hidden w-full my-6 text-center items-center">
        <div v-if="!isEditing">
          <button
            role="button"
            class="text-zyxel-green-800 font-bold cursor-pointer"
            @click="toggleEdit"
            @keydown.enter="toggleEdit"
          >
            {{ $t('messages.common.link_edit_profile') }}
          </button>
        </div>
        <div
          v-else
          class="flex-row block"
        >
          <button
            class="text-zyxel-green-700 font-bold cursor-pointer"
            @click="cancelEdit"
          >
            {{ $t('messages.common.link_cancel') }}
          </button>
          <button
            class="cursor-not-allowed text-zyxel-gray-800 font-bold ml-2"
            :class="{ 'cursor-pointer text-zyxel-green-700': !isDemoAccount }"
            @click="saveEdit"
          >
            {{ $t('messages.common.link_save') }}
          </button>
        </div>
      </div>
      <EmailChangeDialog
        :is-visible="showChangeEmailModal"
        @change="changeEmail"
        @cancel="cancel"
      />
      <DeleteAccountDialog
        :is-show="showDeleteAccountModal"
        @next="next"
        @delete_cancel="delete_cancel"
      />
      <Loading
        v-model:active="isLoading"
        color="#3C9F00"
        loader="spinner"
        background-color="#FFFFFF"
        :is-full-page="true"
      />
    </div>
  </div>
</template>
<script>
import axios from "axios";
import { apiBaseUrl } from "@/config";
import { useAuthStore } from "@/stores/auth";
import CountrySelect from "@/components/CountrySelect.vue";
import EmailChangeDialog from "@/components/EmailChangeDialog.vue";
import DeleteAccountDialog from "@/components/DeleteAccountDialog.vue";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/css/index.css";
import { logger } from '@/utils/logger';

export default {
    name: "ProfileEdit",
    components: {
        Loading,
        CountrySelect,
        EmailChangeDialog,
        DeleteAccountDialog
    },
    props: {
        user: Object
    },
    data() {
        return {
            isEditing: false,
            isLoading: false,
            showChangeEmailModal: false,
            showDeleteAccountModal: false,
            editedUser: {},
            errors: {
                first_name: false,
                last_name: false,
                email: false,
                country: false,
                contact: false
            },
            errors_msg: {
                first_name: false,
                last_name: false,
                email: false,
                country: false,
                contact: false
            }
        }
    },
    computed: {
        userCountryUpper() {
            return this.user && this.user.country ? this.user.country.toUpperCase() : null;
        },
        userCountryLower() {
            return this.user && this.user.country ? this.user.country.toLowerCase() : null;
        },
        isDemoAccount() {
            const authStore = useAuthStore();
            return authStore.isDemoAccount;
        }
    },
    methods: {
        toggleEdit() {
            this.isEditing = !this.isEditing;
            this.editedUser = this.isEditing ? JSON.parse(JSON.stringify(this.user)) : {};
        },
        cancelEdit() {
            this.clearErrors();
            this.isEditing = false;
        },
        async isDisposableEmail() {
            try {
                const response = await axios.post(`${apiBaseUrl}/api/disposable_email/verify`, {
                    email: this.editedUser.email
                });
                if (response.status === 200) {
                    if (response.data.success === false){
                        this.errors.email = true;
                        this.errors_msg.email = "messages.error_msg.disposable_email";
                        return false;
                    } else {
                        return true;
                    }
                } else{
                    this.errors.email = true;
                    this.errors_msg.email = "messages.error_msg.disposable_email";
                    return false;
                }
            } catch (error) {
                this.errors.email = true;
                this.errors_msg.email = "messages.error_msg.disposable_email";
                return false;
            }
        },
        async isDuplicateEmail() {
            try {
                let response_dup = await axios.post(`${apiBaseUrl}/api/users/check_duplicate_email`, {
                    email: this.editedUser.email
                });
                if (response_dup.status === 200) {
                    if (response_dup.data.duplicate === true){
                        this.errors.email = true;
                        this.errors_msg.email = "messages.error_msg.exists_email";
                        return false;
                    } else {
                        return true;
                    }
                } else{
                    this.errors.email = true;
                    this.errors_msg.email = "messages.error_msg.exists_email";
                    return false;
                }
            } catch (error) {
                this.errors.email = true;
                this.errors_msg.email = "messages.error_msg.exists_email";
                return false;
            }
        },
        async saveEdit() {
            this.isLoading = false;
            if (this.isDemoAccount) {
                return;
            }

            if (!this.checkInputs()) {
                return;
            }
            const isEmailValid = await this.isDisposableEmail();
            if (!isEmailValid) {
                logger.error('Email Invalid');
                return;
            }

            const authStore = useAuthStore();

            const userData = {
                first_name: this.editedUser.first_name,
                last_name: this.editedUser.last_name,
                country: this.editedUser.country,
                contact: this.editedUser.contact
            };

            if (this.editedUser.email !== this.user.email){
                const isDuplicateEmail = await this.isDuplicateEmail();
                if (!isDuplicateEmail) {
                    logger.error('This email is a duplicate.');
                    return;
                }
                else {
                    await authStore.updateUserProfile(userData);
                    this.isEditing = false;
                    this.showChangeEmailModal = true;
                }
            } else {
                await authStore.updateUserProfile(userData);
                this.isEditing = false;
            }
        },
        isValidEmail(email) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            return emailRegex.test(email);
        },
        checkInputs() {
            let valid = true;
            this.errors.first_name = false;
            this.errors.last_name = false;
            this.errors.email = false;
            this.errors.contact = false;

            if (this.editedUser.first_name == null || this.editedUser.first_name.trim().length === 0){
                this.errors.first_name = true;
                this.errors_msg.first_name = "messages.error_msg.field_empty";
                valid = false;
            } else if (this.editedUser.first_name.trim().length > 80) {
                this.errors.first_name = true;
                this.errors_msg.first_name = "messages.error_msg.length_80";
                valid = false;
            }

            if (this.editedUser.last_name == null || this.editedUser.last_name.trim().length === 0){
                this.errors.last_name = true;
                this.errors_msg.last_name = "messages.error_msg.field_empty";
                valid = false;
            } else if (this.editedUser.last_name.trim().length > 80) {
                this.errors.last_name = true;
                this.errors_msg.last_name = "messages.error_msg.length_80";
                valid = false;
            }


            if (this.editedUser.email == null || this.editedUser.email.trim().length === 0){
                this.errors.email = true;
                this.errors_msg.email = "messages.error_msg.field_empty";
                valid = false;
            } else if (this.editedUser.email.trim().length > 250) {
                this.errors.email = true;
                this.errors_msg.email = "messages.error_msg.length_250";
                valid = false;
            } else if (!this.isValidEmail(this.editedUser.email.trim())) {
                this.errors.email = true;
                this.errors_msg.email = "messages.error_msg.email_valid";
                valid = false;
            }

            if (!this.editedUser.country) {
                this.errors.country = true;
                valid = false;
            }

            if (this.editedUser.contact != null && this.editedUser.contact.trim().length > 0){
                if (this.editedUser.contact.trim().length > 250) {
                    this.errors.contact = true;
                    this.errors_msg.contact = "messages.error_msg.length_250";
                    valid = false;
                } else if (!this.isValidEmail(this.editedUser.contact.trim())) {
                    this.errors.contact = true;
                    this.errors_msg.contact = "messages.error_msg.email_valid";
                    valid = false;
                }
            }

            return valid;
        },
        clearErrors() {
            this.errors.first_name = false;
            this.errors.last_name = false;
            this.errors.email = false;
            this.errors.country = false;
            this.errors.contact = false;
        },
        cancel(){
            this.isLoading = false;
            this.clearErrors();
            this.isEditing = true;
            this.showChangeEmailModal = false;
        },
        async changeEmail(){
            this.isLoading = true;
            const auth = useAuthStore();
            logger.log("this.editedUser.email" + this.editedUser.email);
            await auth.sendChangeEmail(this.editedUser.email);
            this.showChangeEmailModal = false;
            this.isLoading = false;
            this.$router.push({ name: 'WaitChangeEmail' , query: { to_email: this.editedUser.email }});
        },
        next(){
            window.open("https://forms.office.com/r/msQn6FC5Vf", '_blank');
            this.isEditing = false;
            this.showDeleteAccountModal = false;
        },
        delete_cancel(){
            this.clearErrors();
            this.isEditing = false;
            this.showDeleteAccountModal = false;
        },
        toggleDelete(){
            this.showDeleteAccountModal = true;
        }
    }
}
</script>

<style scoped>
.text-ellipsis {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
</style>